// Become Associate on Course Curriculum is for people who are *already
// Affiliates* applying to Associate (backend:
// AffiliateAssociateApplicationEligibility). It is not prospect → Associate
// and not terminated-Associate re-register.
//
// Acceptable Progress for this persona is Affiliate standing. The PAT
// visibility path also requires standing that is not Acceptable Progress
// Termination (`standing.showProgressStandingNotice` covers
// Progress-Privileged; `standing.showReapplyPathOffer` covers
// Reapply-Gated). skipAssociateCtaQueries only skips Reapply-Gated when
// account-terminated-notice is on, so flag-off GK Affiliates still reach
// this helper. Typical registered Affiliates completed F0 and never
// started L1, so programExpirationDate is null (or Qualifying Level's
// 9-month clock). Show the link when they have a qualifying PAT and are
// still a registered Affiliate. The L1+ expiration check is a defensive
// hide if Associate progress is already on the payload while they remain
// on the Affiliate dashboard. Missing L1+ programExpirationDate fails
// closed (hide the PAT path).

const QUALIFYING_PAT_STATUSES = ["qualified", "complete"]

const ASSOCIATE_PROGRESS_LEVEL_KEYS = ["level1", "level2", "level3"]

function calendarDateString(value) {
  if (value instanceof Date) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, "0")
    const day = String(value.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  if (typeof value !== "string") {
    return null
  }

  const dateOnly = value.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : null
}

function isAssociateProgressLevelKey(key) {
  return ASSOCIATE_PROGRESS_LEVEL_KEYS.includes(key)
}

function hasStartedAssociateLevelProgress(pgmProgress) {
  if (isAssociateProgressLevelKey(pgmProgress?.currentLevelKey)) {
    return true
  }

  if (isAssociateProgressLevelKey(pgmProgress?.mostRecentCompletedLevelKey)) {
    return true
  }

  const levels = pgmProgress?.levels
  if (!Array.isArray(levels)) {
    return false
  }

  return levels.some(level => {
    if (
      !isAssociateProgressLevelKey(level?.key) &&
      level?.numeral !== 1 &&
      level?.numeral !== 2 &&
      level?.numeral !== 3
    ) {
      return false
    }

    if (level.startDate) {
      return true
    }

    const status = level.status
    return (
      typeof status === "string" &&
      status !== "not_started" &&
      status !== "locked"
    )
  })
}

export function hasQualifyingPat(pgmProgress) {
  const patStatus = pgmProgress?.patStatus
  return (
    typeof patStatus === "string" && QUALIFYING_PAT_STATUSES.includes(patStatus)
  )
}

export function isWithinAcceptableProgressWindow(
  pgmProgress,
  asOf = new Date(),
) {
  // Registered Affiliates who have not started Associate L1+ are in-window:
  // their AP standing is Affiliate membership. Do not treat F0's 9-month
  // QL date as AP. Acceptable Progress Termination standing is refused by
  // the PAT path in shouldShowAffiliateAssociateApplicationLink, not here.
  if (!hasStartedAssociateLevelProgress(pgmProgress)) {
    return true
  }

  const expirationDate = calendarDateString(pgmProgress?.programExpirationDate)
  if (!expirationDate) {
    return false
  }

  const asOfDate = calendarDateString(asOf)
  if (!asOfDate) {
    return false
  }

  return expirationDate >= asOfDate
}

export function shouldShowAffiliateAssociateApplicationLink({
  pgmProgress,
  eligibilityEligible,
  standing,
} = {}) {
  if (eligibilityEligible === true) {
    return true
  }

  if (standing?.showReapplyPathOffer || standing?.showProgressStandingNotice) {
    return false
  }

  return (
    hasQualifyingPat(pgmProgress) &&
    isWithinAcceptableProgressWindow(pgmProgress)
  )
}
